<template>
  <div>
    <div v-if="showNewNote">
      <note-create
        :contact-id="contactId"
        :lead-id="leadId"
        :task-id="taskId"
        :reservation-id="reservationId"
        @newNote="saveNewNote"
      ></note-create>
    </div>
    <v-data-table
      :items="
        notes.filter(
          (data) =>
            !search || data.notes.toLowerCase().includes(search.toLowerCase())
        )
      "
      :headers="headers"
      :loading="loading"
      density="compact"
      item-value="id"
      v-model:expanded="expandedItems"
      show-expand
      :expanded-rows="openNotes ? notes.map((note) => note.id) : []"
      hide-default-footer
    >
      <!-- Search field in header -->
      <template v-slot:top>
        <v-row class="px-4 py-2" :dense="iconMode">
          <v-col :cols="iconMode ? 10 : 8" :sm="iconMode ? 9 : 6">
            <v-text-field
              v-if="notes.length > 5"
              v-model="search"
              density="compact"
              placeholder="Type here to search"
              hide-details
              variant="outlined"
            ></v-text-field>
          </v-col>
          <v-col :cols="iconMode ? 2 : 4" :sm="iconMode ? 3 : 6" class="d-flex justify-end">
            <v-tooltip v-if="iconMode && !showNewNote" text="Create new note" location="top">
              <template v-slot:activator="{ props }">
                <v-btn
                  v-bind="props"
                  icon="mdi-plus"
                  size="small"
                  variant="text"
                  @click="showNewNote = true"
                ></v-btn>
              </template>
            </v-tooltip>
            <v-btn v-else-if="!showNewNote" size="small" @click="showNewNote = true">
              New note
            </v-btn>
          </v-col>
        </v-row>
      </template>

      <!-- Expanded content -->
      <template v-slot:expanded-row="{ item, columns }">
        <td :colspan="columns.length">
          <div
            class="note-drop-zone pa-4"
            :class="{
              'note-highlight': modifiedNoteIds.has(item.id),
              'is-drag-active': isDrag,
            }"
            @drop.prevent="handleRowDrop($event, item)"
            @dragover.prevent
            @dragenter.prevent="handleRowDragState($event, true)"
            @dragleave.prevent="handleRowDragState($event, false)"
          >
            <div class="text-block mb-4">
              {{ item.notes }}
            </div>
            <div class="attachments">
              <assets-grid
                :assets="item.assets"
                :allow-delete="true"
                @delete-asset="(asset) => handleAssetDelete(asset, item.id)"
              ></assets-grid>
            </div>

            <!-- Drop indicator overlay -->
            <div v-if="isDrag" class="drop-indicator">
              <v-icon size="x-large" color="primary" class="mb-2"
                >mdi-cloud-upload</v-icon
              >
              <span>Drop files here to add to this note</span>
            </div>
          </div>
        </td>
      </template>

      <!-- User info column -->
      <template v-slot:item.userInfo="{ item }">
        <div
          :class="{ 'note-highlight': modifiedNoteIds.has(item.id) }"
          class="d-flex justify-space-between align-center"
        >
          <div class="d-flex align-center">
            <v-avatar size="24" class="mr-2">
              <v-img
                :src="getPhotoUrl(getMemberFromId(item.user_id).photo_asset_id)"
                cover
              ></v-img>
            </v-avatar>
            <span>
              {{ getMemberFromId(item.user_id).first_name }}
              {{ getMemberFromId(item.user_id).last_name }}
            </span>
          </div>
          <div>
            <span class="text-caption">{{
              cleanNotesTime(item.created_at)
            }}</span>
          </div>
        </div>
        <div v-if="!isItemExpanded(item.id)" class="pt-2">
          {{ previewLine(item.notes, 85) }}
        </div>
      </template>

      <!-- Actions column -->
      <template v-slot:item.actions="{ item }">
        <div
          :class="{
            'note-highlight': modifiedNoteIds.has(item.id),
            'actions-container': true,
          }"
          class="d-flex justify-end align-center"
        >
          <v-badge
            v-if="item.assets?.length > 0"
            :content="item.assets?.length"
            color="success"
            location="top end"
            class="me-2"
          >
            <v-icon>mdi-paperclip</v-icon>
          </v-badge>

          <v-btn-group v-if="!iconMode">
            <v-btn size="small" @click="edit(item.id, item.notes)">
              Edit
            </v-btn>

            <v-btn size="small" @click="showAssetUpload(item.id)">
              <v-icon>mdi-upload</v-icon>
              Upload
            </v-btn>
          </v-btn-group>

          <template v-else>
            <v-tooltip text="Edit note" location="top">
              <template v-slot:activator="{ props }">
                <v-btn
                  v-bind="props"
                  icon="mdi-pencil"
                  size="small"
                  variant="text"
                  @click="edit(item.id, item.notes)"
                ></v-btn>
              </template>
            </v-tooltip>
            <v-tooltip text="Upload file" location="top">
              <template v-slot:activator="{ props }">
                <v-btn
                  v-bind="props"
                  icon="mdi-upload"
                  size="small"
                  variant="text"
                  @click="showAssetUpload(item.id)"
                ></v-btn>
              </template>
            </v-tooltip>
          </template>
        </div>
      </template>

      <!-- No data placeholder -->
      <template v-slot:no-data>
        <div class="text-center py-4">No Notes Added</div>
      </template>
    </v-data-table>

    <!-- Edit Note -->
    <v-dialog v-model="showEditDialog" max-width="600">
      <v-card>
        <v-card-title>Edit Note</v-card-title>
        <v-card-text>
          <v-textarea
            v-model="editNote"
            rows="4"
            auto-grow
            class="no-word-break"
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-btn color="error" size="small" @click="deleteNote()">
            Delete
          </v-btn>

          <v-spacer></v-spacer>

          <content-block-select
            v-if="showQuickReplyEdit"
            :use-menu="true"
            :replyType="'text'"
            :size="'small'"
            @updateContent="editNoteMerge"
          >
          </content-block-select>

          <v-btn
            v-if="getTextBlocks.length && contactId"
            size="small"
            @click="showQuickReplyEdit = !showQuickReplyEdit"
          >
            <v-icon class="mr-1">mdi-comment</v-icon> Quick Reply
          </v-btn>

          <v-divider vertical class="mx-2"></v-divider>

          <v-btn size="small" variant="text" @click="showEditDialog = false">
            Cancel
          </v-btn>

          <v-btn color="primary" size="small" @click="updateNote()">
            Update Note
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Asset library -->
    <el-dialog :title="'Select File'" v-model="selectFile">
      <asset-library
        :asset-types="['image', 'pdf', 'audio', 'video']"
        @update="handleAssetAdd"
        @close="selectFile = false"
        :thumbnail-mode="true"
        :limit="5"
      ></asset-library>
    </el-dialog>
  </div>
</template>
<script>
import { toRaw } from 'vue';
import { mapGetters } from 'vuex';
import { ElLoading } from 'element-plus';
import { uploadAsset } from 'src/util/assetUtils.js';

import AssetLibrary from 'src/components/UIComponents/AssetLibrary';
import AssetsGrid from 'src/components/Dashboard/Views/Assets/AssetsGrid.vue';
import AssetInlineUpload from 'src/components/UIComponents/AssetInlineUpload.vue';
import ContentBlockSelect from 'src/components/UIComponents/ContentBlockSelect';
import NoteCreate from 'src/components/UIComponents/NoteCreate.vue';

import notesServices from 'src/services/notesServices';
import templateService from 'src/services/templateServices';
import { processError } from 'src/util/processError.js';

export default {
  components: {
    AssetLibrary,
    AssetsGrid,
    AssetInlineUpload,
    ContentBlockSelect,
    NoteCreate,
  },
  computed: {
    ...mapGetters('team', ['getTeam']),
    ...mapGetters('userPhotos', ['getPhotoUrl']),
    memberMap() {
      const map = {};
      for (const member of this.getTeam) {
        map[member.id] = member;
      }
      return map;
    },
    ...mapGetters('appConfig', ['getAppConfig']),
    ...mapGetters('contentBlocks', ['getTextBlocks']),
    openNotes() {
      return this.getAppConfig?.lead?.openNotes ?? false;
    },
    expandedRowKeys() {
      return this.openNotes ? this.notes.map((row) => row.id) : [];
    },
    getNoteTypeId() {
      switch (this.relationshipType) {
        case 'contact':
          return this.contactId;
        case 'lead':
          return this.leadId;
        case 'task':
          return this.taskId;
        case 'conversation':
          return this.conversationId;
        case 'reservation':
          return this.reservationId;
        default:
          throw new Error('Invalid relationship type', this.relationshipType);
      }
    },
  },
  props: {
    /*
     we have deprecated the generic [id] and [type] in favor for explicit contactId, leadId and taskId.
     but we will still have a relationshipType prop, that will be used to determine the type of note.
     */
    relationshipType: {
      type: String,
      default: () => 'contact',
    },
    contactId: {
      type: Number,
    },
    leadId: {
      type: Number,
    },
    taskId: {
      type: Number,
    },
    conversationId: {
      type: Number,
      required: false,
    },
    reservationId: {
      type: Number,
      required: false,
    },
    notesData: {
      type: Array,
      default: () => undefined,
    },
    assetIsPublic: {
      type: Boolean,
      default: () => false,
    },
    iconMode: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      notes: [],
      showNewNote: false,
      newNote: '',
      editId: 0,
      editNote: '',
      search: '',
      showEditDialog: false,
      attachFiles: [],
      selectFile: false,
      modifiedNoteIds: new Set(),
      // Drag state
      isDrag: false,
      assetNoteId: 0,
      // Show Quick Replies.
      showQuickReply: false,
      showQuickReplyEdit: false,
      // in instances where both the lead & contact id change
      reloadNotesLock: false,

      headers: [
        {
          title: 'Notes',
          key: 'userInfo',
          sortable: false,
        },
        {
          title: 'Actions',
          key: 'actions',
          sortable: false,
          align: 'end',
          width: '180',
        },
      ],
      expandedItems: [],
    };
  },
  watch: {
    getTeam: {
      immediate: true,
      handler(list) {
        const ids = (list || []).map((u) => u.photo_asset_id).filter(Boolean);
        if (ids.length) {
          this.$store.dispatch('userPhotos/resolvePhotoUrls', ids);
        }
      },
    },
    contactId(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.reloadNotes();
      }
    },
    leadId(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.reloadNotes();
      }
    },
    taskId(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.reloadNotes();
      }
    },
    reservationId(newVal, oldVal) {
      // Also the merge context for other types, only reload our own notes
      if (this.relationshipType == 'reservation' && newVal !== oldVal) {
        this.reloadNotes();
      }
    },
    openNotes(val) {
      if (val) {
        this.expandedItems = this.notes.map((note) => note.id);
      } else {
        this.expandedItems = [];
      }
    },
  },
  async created() {
    if (this.notesData) {
      this.notes = this.notesData;
      return;
    }
    this.loadNotes();
  },
  mounted() {
    window.addEventListener('dragenter', this.handleDragEnter);
    window.addEventListener('dragleave', this.handleDragLeave);
    if (this.openNotes) {
      this.expandedItems = this.notes.map((note) => note.id);
    }
  },
  unmounted() {
    window.removeEventListener('dragenter', this.handleDragEnter);
    window.removeEventListener('dragleave', this.handleDragLeave);
  },
  methods: {
    isItemExpanded(itemId) {
      return this.expandedItems.includes(itemId);
    },
    async reloadNotes() {
      if (this.reloadNotesLock) {
        return;
      }
      this.reloadNotesLock = true;
      await this.loadNotes();
      this.reloadNotesLock = false;
    },

    async saveNewNote(newNote) {
      if (!newNote) {
        this.showNewNote = false;
        return;
      }

      const result = await notesServices.newNote(
        this.relationshipType,
        this.getNoteTypeId,
        newNote.note
      );
      const note = result.data;
      // Link the note too,
      // TODO: Add this to the api above
      if (newNote.attachments.length) {
        const finalizedNote = await notesServices.updateAssets(note.id, {
          assets: toRaw(newNote.attachments).map((a) => {
            return { id: a.id };
          }),
        });
        note.assets = finalizedNote.data.assets;
      }
      this.notes.unshift(note);
      this.showNewNote = false;
    },
    async handleAssetAdd(attach) {
      if (!this.selectedNote) {
        console.error('no selected note set, cant save');
        return false;
      }
      const curNote = this.notes.find((n) => n.id == this.selectedNote);
      if (!curNote) {
        console.error('no current note, not updating');
        return false;
      }
      const assets = toRaw(curNote.assets);
      const a = assets.map((a) => {
        return { id: a.id };
      });
      a.push({ id: attach.id });
      const finalizedNote = await notesServices.updateAssets(
        this.selectedNote,
        {
          assets: a,
        }
      );
      // Only push to the current MV, if you replace the array you lose reactivity
      curNote.assets.push(attach);
    },

    highlightNote(noteId) {
      this.modifiedNoteIds.add(noteId);
      setTimeout(() => {
        this.modifiedNoteIds.delete(noteId);
      }, 3000);
    },

    async handleRowDrop(event, row) {
      event.preventDefault();
      this.isDrag = false;

      if (!event.dataTransfer.files.length) return;

      // Set the selected note for the upload
      this.selectedNote = row.id;

      const loading = ElLoading.service({
        lock: true,
        text: 'Loading',
        background: 'rgba(255, 255, 255, 0.7)',
      });

      try {
        for (const file of Array.from(event.dataTransfer.files)) {
          file.isPublic = this.assetIsPublic;
          const { success, asset, error } = await uploadAsset(file);

          if (success) {
            this.attachFiles.push(asset);
          } else {
            this.$message({
              type: 'error',
              message: `Error uploading file: ${file.name}`,
            });
          }
        }

        await this.connectFile();
      } catch (error) {
        console.error('Error uploading files:', error);
        this.$message({
          type: 'error',
          message: 'Error uploading files: ' + error.message,
        });
      } finally {
        this.attachFiles = [];
        loading.close();
      }
    },

    handleRowDragState(event, entering) {
      event.preventDefault();
      const dropZone = event.currentTarget;

      if (entering) {
        dropZone.classList.add('is-drag-target');
      } else {
        const rect = dropZone.getBoundingClientRect();
        if (
          event.clientX <= rect.left ||
          event.clientX >= rect.right ||
          event.clientY <= rect.top ||
          event.clientY >= rect.bottom
        ) {
          dropZone.classList.remove('is-drag-target');
        }
      }
    },

    // handle Drag for new note.
    handleDragEnter(e) {
      e.preventDefault();
      if (e.dataTransfer.types.includes('Files')) {
        this.isDrag = true;
      }
    },

    // handle leave for new note.
    handleDragLeave(e) {
      e.preventDefault();
      // Only hide if we're leaving the window
      if (
        e.clientX <= 0 ||
        e.clientX >= window.innerWidth ||
        e.clientY <= 0 ||
        e.clientY >= window.innerHeight
      ) {
        this.isDrag = false;
      }
    },

    showAssetUpload(id) {
      this.selectFile = true;
      this.selectedNote = id;
    },

    async connectFile() {
      // Error checks.
      if (!this.selectedNote) {
        this.$message({
          type: 'error',
          message: 'No note selected to attach file to.',
        });
        return;
      }

      if (!this.attachFiles.length) {
        this.$message({
          type: 'error',
          message: 'No files selected to attach to note.',
        });
        return;
      }

      // finally proceed with attached the asset to the note.
      const sourceNote = this.notes.find((n) => n.id === this.selectedNote);
      if (!sourceNote) {
        this.$message({
          type: 'error',
          message: "Couldn't find the note to attach file to.",
        });
        return;
      }

      try {
        const finalizedNote = await notesServices.updateAssets(
          this.selectedNote,
          {
            assets: [...(sourceNote.assets || []), ...this.attachFiles],
          }
        );

        if (!finalizedNote?.data || finalizedNote.status !== 200) {
          throw new Error('Invalid response from server');
        }

        // Update the note with new assets
        const noteIndex = this.notes.findIndex(
          (n) => n.id === this.selectedNote
        );
        if (noteIndex !== -1) {
          this.notes[noteIndex].assets = finalizedNote.data.assets;
          this.highlightNote(this.selectedNote);
        }

        // Reset file selection state
        this.selectFile = false;
        this.attachFiles = [];
      } catch (error) {
        console.error('Error updating note with new file:', error);
        this.attachFiles = [];
        this.$message({
          type: 'error',
          message: 'Error attaching image to note.',
        });
      }
    },
    async handleAssetDelete(input, noteId) {
      const sourceNote = this.notes.find((n) => n.id === noteId);
      if (!sourceNote) return;

      try {
        const filteredAssets = sourceNote.assets.filter((a) => {
          return String(a.id) !== String(input.id);
        });

        const finalizedNote = await notesServices.updateAssets(noteId, {
          assets: filteredAssets,
        });

        if (!finalizedNote?.data || finalizedNote.status !== 200) {
          throw new Error('Invalid response from server');
        }

        // Update the note without the asset
        const noteIndex = this.notes.findIndex((n) => n.id === noteId);
        if (noteIndex !== -1) {
          this.notes[noteIndex].assets = finalizedNote.data.assets;
        }
      } catch (error) {
        console.error('Error removing image from note:', error);
        this.$message({
          type: 'error',
          message: 'Error removing image from note.',
        });
      }
    },
    handleNewAssetDelete(input) {
      const filteredAssets = this.attachFiles.filter((a) => {
        return String(a.id) !== String(input.id);
      });
      this.attachFiles = filteredAssets;
    },
    edit(id, notes) {
      this.editId = id;
      this.editNote = notes;
      this.showEditDialog = true;
    },
    getMemberFromId(id) {
      return this.memberMap[id] || { first_name: 'unknown', last_name: 'user' };
    },
    async loadNotes() {
      if (this.getNoteTypeId == 0 || this.getNoteTypeId == undefined) {
        this.loading = false;
        return;
      }
      this.loading = true;
      this.notes = [];
      const notes = await notesServices.fetchNotes(
        this.relationshipType,
        this.getNoteTypeId
      );
      this.notes = notes.data;
      this.loading = false;
    },
    cleanNotesTime(time) {
      const date = new Date(time);
      // TODO: get the locality of the browser
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
      });
    },
    async updateNote() {
      const response = await notesServices.updateNote(this.editId, {
        notes: this.editNote,
      });
      // Update locally instead of reloading
      const noteIndex = this.notes.findIndex((note) => note.id === this.editId);
      if (noteIndex !== -1) {
        this.notes[noteIndex].notes = this.editNote;
      }
      this.showEditDialog = false;
      this.highlightNote(this.editId);
    },
    async deleteNote() {
      this.$confirm('Are you sure you want to delete this note?', 'Warning', {
        confirmButtonText: 'Yes, Delete',
        cancelButtonText: 'Cancel',
        type: 'warning',
      })
        .then(async () => {
          await notesServices.deleteNote(this.editId);
          this.showEditDialog = false;
          this.loadNotes();

          this.$message({
            message: 'Congrats, your note is now deleted. 🙌',
            type: 'success',
          });
        })
        .catch((e) => {
          // The confirm rejects with 'cancel', anything else is a real failure
          if (e === 'cancel' || e === 'close') {
            this.$message({
              type: 'info',
              message: 'Delete canceled',
            });
            return;
          }
          this.$message({
            type: 'error',
            message: 'Error deleting note: ' + processError(e.response ?? e),
            showClose: true,
          });
        });
    },
    previewLine(input, maxLen, separator = ' ') {
      if (input.length <= maxLen) return input;
      return input.substr(0, input.lastIndexOf(separator, maxLen)) + '...';
    },
    editNoteMerge(content) {
      this.mergeMessage(content, true);
    },

    async mergeMessage(content, edit = false) {
      try {
        const result = await templateService.getTemplateMerge(
          0,
          this.contactId,
          undefined, // campaignId
          this.reservationId, // reservationId
          this.leadId,
          content
        );
      } catch (e) {
        if (!e.response) {
          throw e;
        }
        if (e.response.status == 422) {
          const result = e.response.data;
          this.$message({
            type: 'error',
            message:
              'Error Parsing Template at this line: <br><br>' +
                result.friendly_error ?? result.status,
            showClose: true,
            // Love me some danger
            dangerouslyUseHTMLString: true,
          });
        } else {
          this.$message({
            type: 'error',
            message: 'Unexpected Error Parsing Template',
            showClose: true,
          });
        }
        return false;
      }

      if (edit === true) {
        this.editNote = String(this.editNote).concat(data.rich_content);
      } else {
        this.newNote = String(this.newNote).concat(data.rich_content);
      }
    },
  },
};
</script>
<style scoped lang="scss">
// attachments
.actions-container {
  display: flex;
  align-items: center;
  justify-content: end;
  gap: 8px;
  white-space: nowrap;
}

.attachment-icon {
  display: flex;
  align-items: center;
}

.button-group {
  display: inline-flex;
}

.text-block {
  white-space: break-spaces; // or pre-line
  word-break: keep-all;
}
.no-word-break {
  word-break: keep-all;
}

.item {
  margin-top: 10px;
  margin-right: 10px;
}

// Note drop zone
.note-drop-zone {
  position: relative;
  min-height: 100px;
  transition: all 0.3s ease;

  &.is-drag-active {
    background-color: rgba(64, 158, 255, 0.1);
    border: 2px dashed #409eff;
    border-radius: 4px;
  }
}

.drop-indicator {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;

  .drop-icon {
    font-size: 24px;
    color: #409eff;
    margin-bottom: 8px;
  }

  span {
    color: #606266;
    font-size: 14px;
  }
}

// on change of note, highlight the note.
@keyframes highlightFade {
  0% {
    background-color: rgba(103, 194, 58, 0.2);
  }
  100% {
    background-color: transparent;
  }
}

.note-highlight {
  animation: highlightFade 3s ease-out;
}
</style>
